import React from 'react'
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { Scores } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

type RoiResultsEntryProps = {
  scores: Scores
  onViewAnswersClick: (assessmentName: string) => void
  isParentReport?: boolean
  isLastItem?: boolean
}

const TableRow = styled.View<{ theme: ThemeType; isLastItem?: boolean }>(
  ({ theme: { colors, spacing }, isLastItem }) => ({
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: spacing['24px'],
    borderBottomWidth: isLastItem ? '0' : '1px',
    borderBottomColor: isLastItem ? undefined : colors.borderDefault,
  }),
)

const AssessmentContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '320px',
  alignItems: 'center',
})

const ViewAnswersButton = styled(TextButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
  marginTop: theme.spacing['6px'],
}))

const ScoreContentContainer = styled.View({
  flexDirection: 'row',
  flexBasis: '80px',
})

const QuestionContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginTop: spacing['8px'],
}))

const SUBSCORE_LABELS = defineMessages({
  erVisits: { defaultMessage: 'ER Visits', description: 'ROI subscore label for ER visits' },
  hlocWeeks: { defaultMessage: 'HLOC Weeks', description: 'ROI subscore label for higher level of care weeks' },
  outpatientVisits: { defaultMessage: 'Outpatient Visits', description: 'ROI subscore label for outpatient visits' },
  partialIopResidentialWeeks: {
    defaultMessage: 'Partial/IOP/Residential Weeks',
    description: 'ROI subscore label for partial, IOP, or residential weeks',
  },
})

const ROI_SUBSCORES: Record<'ROI' | 'ROI-P', { key: keyof Scores; label: MessageDescriptor }[]> = {
  ROI: [
    { key: 'ROI-ER-VISITS', label: SUBSCORE_LABELS.erVisits },
    { key: 'ROI-HLOC-WEEKS', label: SUBSCORE_LABELS.hlocWeeks },
    { key: 'ROI-OUTPATIENT-VISITS', label: SUBSCORE_LABELS.outpatientVisits },
    { key: 'ROI-PARTIAL-IOP-RESIDENTIAL-WEEKS', label: SUBSCORE_LABELS.partialIopResidentialWeeks },
  ],
  'ROI-P': [
    { key: 'ROI-P-ER-VISITS', label: SUBSCORE_LABELS.erVisits },
    { key: 'ROI-P-HLOC-WEEKS', label: SUBSCORE_LABELS.hlocWeeks },
    { key: 'ROI-P-OUTPATIENT-VISITS', label: SUBSCORE_LABELS.outpatientVisits },
    { key: 'ROI-P-PARTIAL-IOP-RESIDENTIAL-WEEKS', label: SUBSCORE_LABELS.partialIopResidentialWeeks },
  ],
}

export const RoiResultsEntry: React.FC<RoiResultsEntryProps> = ({
  scores,
  isLastItem,
  onViewAnswersClick,
  isParentReport,
}) => {
  const theme = useTheme()
  const { formatMessage } = useIntl()

  const prefix = isParentReport ? 'ROI-P' : 'ROI'
  const testIDSuffix = isParentReport ? 'roi-p' : 'roi'
  const roiSubscores = ROI_SUBSCORES[prefix]

  const hasAnySubscore = roiSubscores.some(({ key }) => scores[key] !== undefined && scores[key] !== null)
  if (!hasAnySubscore) return null

  return (
    <TableRow testID={tID(`ClientOutcomes-results-${testIDSuffix}`)} isLastItem={isLastItem}>
      <AssessmentContentContainer>
        <Title
          text={formatMessage({
            defaultMessage: 'Treatment Utilization',
            description: 'Return on investment assessment title',
          })}
          size={TitleSize.MEDIUM}
        />
        <ViewAnswersButton
          testID={tID(`ClientOutcomes-viewAnswers-${testIDSuffix}`)}
          onPress={() => {
            onViewAnswersClick(`${prefix}-TOTAL`)
          }}
          text='view answers'
        />
      </AssessmentContentContainer>
      <ScoreContentContainer />
      <View>
        {roiSubscores.map(({ key, label }) => {
          const value = scores[key]
          if (value === undefined || value === null) return null
          return (
            <QuestionContainer key={key}>
              <Title text={formatMessage(label)} size={TitleSize.XSMALL} />
              <BodyText text={`${value}`} size={BodyTextSize.DEFAULT} color={theme.colors.textSecondary} />
            </QuestionContainer>
          )
        })}
      </View>
    </TableRow>
  )
}
